import { i18n } from "@lingui/core";
import { i18nIsNumericPrefixString } from "@regrello/core-utils/src/utils/numberFormatUtils";
import type { FieldInstanceFields } from "@regrello/graphql-api";
import {
  type RegrelloDataGridV2EditableCellProps,
  useDataGridV2EditableCell,
  useDataGridV2FirstKeystroke,
} from "@regrello/ui-core";
import { useRef } from "react";
import { isWithinElement } from "../../../../../../../utils/isWithinElement";
import { RegrelloFormFieldNumber } from "../../../../formFields/RegrelloFormFieldNumber";
import type { NumberFieldPluginFrontendValue } from "./types";

/**
 * An editable cell in which the user can input a number. Intended for use in `RegrelloDataGridV2`.
 *
 * This cell editor allows passthrough of non-number keys, such as the minus sign and the decimal
 * separator so that those eventkeys can be included in the RegrelloFormFieldNumber input on mount.
 */
export function RegrelloEditableCellNumber({
  api,
  eventKey,
  fieldInstance,
  formatOptions,
  onValueChange,
  value,
}: RegrelloDataGridV2EditableCellProps<RegrelloEditableCellNumberProps>) {
  const editableCellRef = useRef<HTMLDivElement>(null);

  if (fieldInstance.spectrumFieldVersion == null) {
    throw new Error("spectrumFieldVersion is unexpectedly not defined");
  }

  const { columnWidth, onKeyDownCapture } = useDataGridV2EditableCell({ api, eventKey });

  const { inputRef } = useDataGridV2FirstKeystroke({
    eventKey,
    onValueChange,
    parseEventKey,
    suppressWritingNull: true,
  });

  return (
    // Number fields are full of content and are difficult to shrink.
    <div ref={editableCellRef} className="min-w-42" style={{ width: columnWidth }}>
      <RegrelloFormFieldNumber
        autoFocus={true}
        defaultStringPrefixValue={
          eventKey != null && i18nIsNumericPrefixString(eventKey, i18n.locale, formatOptions?.maximumFractionDigits)
            ? eventKey
            : undefined
        }
        formatOptions={formatOptions}
        hideIncrementButtons={true}
        inputClassName="rounded-none"
        inputRef={inputRef}
        isDefaultMarginsOmitted={true}
        onBlur={(event) => {
          const isWithinInput = isWithinElement(
            event.relatedTarget as HTMLElement | null | undefined,
            editableCellRef.current,
          );

          if (!isWithinInput) {
            api.stopEditing();
          }
        }}
        onKeyDownCapture={onKeyDownCapture}
        onValueChange={onValueChange}
        size="x-small"
        // (jullman): Only suppress when a valid number is provided on mount to prevent the first
        // keystroke from triggering formatting while the user is typing. Opening the cell otherwise
        // preserves formatting.
        suppressDecimalFormattingOnInitialInteraction={eventKey != null && parseEventKey(eventKey) != null}
        value={getInitialValue(eventKey, i18n.locale, value, formatOptions)}
      />
    </div>
  );
}

export interface RegrelloEditableCellNumberProps {
  /** The field instance that corresponds to the current table column. */
  fieldInstance: FieldInstanceFields;

  /** Format options for controlling decimal places and thousands separators. */
  formatOptions?: Intl.NumberFormatOptions;

  /** The callback to invoke when the user changes the value of the cell. */
  onValueChange: (value: NumberFieldPluginFrontendValue) => void;

  /**
   * The current value to display in the cell. This is the value before editing and the value that
   * will be reinstated if editing is canceled.
   */
  value: NumberFieldPluginFrontendValue;
}

/**
 * Parse the event key into a number or null.
 */
function parseEventKey(eventKey: string): number | null {
  const keyValue = Number.parseFloat(eventKey);
  if (Number.isNaN(keyValue)) {
    return null;
  }

  return keyValue;
}

/**
 * Get the initial value to display in the cell.
 */
function getInitialValue(
  eventKey: string | null,
  locale: string,
  value: NumberFieldPluginFrontendValue,
  formatOptions?: Intl.NumberFormatOptions,
) {
  if (eventKey == null) {
    return value;
  }
  if (i18nIsNumericPrefixString(eventKey, locale, formatOptions?.maximumFractionDigits)) {
    return undefined;
  }
  return parseEventKey(eventKey) ?? value;
}
